import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Post } from "@/types/posts";
import { Link, MinusCircle, PlusCircle, UserXmark, WebWindowXmark, Edit, Trash } from "iconoir-react";
import { useSetAtom } from 'jotai';
import { showBottomNavAtom } from '@/stores/ui.atom';
import { useEffect } from 'react';

interface PostOptionsSheetProps {
  post: Post;
  isOpen: boolean;
  onClose: () => void;
  isOwnPost?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function PostOptionsSheet({ post, isOpen, onClose }: PostOptionsSheetProps) {
  const setShowBottomNav = useSetAtom(showBottomNavAtom);

  useEffect(() => {
    setShowBottomNav(!isOpen);
    return () => setShowBottomNav(true);
  }, [isOpen, setShowBottomNav]);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="bottom"
        className="bg-[#FBF6F1] rounded-t-xl px-4 pt-3 pb-6 max-h-[85vh] overflow-y-auto"
      >
        <div className="w-full mx-auto flex flex-col justify-start items-center gap-1.5">
          <div className="w-20 h-[5px]  rounded-[100px] opacity-20"></div>
          <div className="self-stretch bg-[#FBF6F1] rounded-md shadow-[0px_1px_4px_0px_rgba(12,12,13,0.05)] flex flex-col justify-start items-start mt-4">
            <div className="self-stretch px-4 py-2 flex flex-col justify-start items-start gap-3">
              <div className="inline-flex justify-start items-start gap-2">
                <div className="w-6 h-6 flex items-center justify-center -ml-2">
                  <PlusCircle className="w-4 h-4" />
                </div>
                <div className="inline-flex flex-col justify-center items-start gap-0.5 ml-1">
                  <div className="justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-none">Quan tâm</div>
                  <div className="justify-start text-[#6F6F6B] text-xs font-normal font-sora leading-none">Bạn sẽ nhìn thấy nhiều bài viết tương tự hơn</div>
                </div>
              </div>
              <div className="inline-flex justify-start items-start gap-2">
                <div className="w-6 h-6 flex items-center justify-center -ml-2">
                  <MinusCircle className="w-4 h-4" />
                </div>
                <div className="inline-flex flex-col justify-center items-start gap-0.5 ml-1">
                  <div className="justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-none">Không quan tâm</div>
                  <div className="justify-start text-[#6F6F6B] text-xs font-normal font-sora leading-none">Bạn sẽ nhìn thấy ít bài viết tương tự hơn</div>
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch bg-[#FBF6F1] rounded-md shadow-[0px_1px_4px_0px_rgba(12,12,13,0.05)] flex flex-col justify-start items-start mt-1.5">
            <div className="self-stretch px-4 py-2 flex flex-col justify-start items-start gap-3">
              <div className="inline-flex justify-start items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center -ml-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M3.75 15.75V3.75C3.75 2.92157 4.42157 2.25 5.25 2.25H12.75C13.5784 2.25 14.25 2.92157 14.25 3.75V15.75L9.81113 12.8965C9.31703 12.5788 8.68297 12.5788 8.18887 12.8965L3.75 15.75Z" stroke="#1E3A3A" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-none ml-1">Lưu bài viết</div>
              </div>
              <div className="inline-flex justify-start items-start gap-2">
                <div className="w-6 h-6 flex items-center justify-center -ml-2">
                  <WebWindowXmark className="w-4 h-4" />
                </div>
                <div className="inline-flex flex-col justify-center items-start gap-0.5 ml-1">
                  <div className="justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-none">Ẩn bài viết</div>
                  <div className="justify-start text-[#6F6F6B] text-xs font-normal font-sora leading-none">Ẩn bớt các bài viết tương tự</div>
                </div>
              </div>
              <div className="self-stretch inline-flex justify-start items-start gap-2">
                <div className="w-6 h-6 flex items-center justify-center -ml-2">
                  <UserXmark className="w-4 h-4" />
                </div>
                <div className="flex-1 inline-flex flex-col justify-center items-start gap-0.5 ml-1">
                  <div className="justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-none">Chặn trang cá nhân của {post.author.display_name || "Kiki"}</div>
                  <div className="self-stretch justify-start text-[#6F6F6B] text-xs font-normal font-sora leading-none">Các bạn sẽ không thể nhìn thấy hoặc liên hệ với nhau</div>
                </div>
              </div>
              <div className="inline-flex justify-start items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center -ml-2">
                  <Link className="w-4 h-4" />
                </div>
                <div className="justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-none ml-1">Sao chép liên kết</div>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function PostOwnerOptionsSheet({ isOpen, onClose, onEdit, onDelete }: { isOpen: boolean, onClose: () => void, onEdit?: (e?: React.MouseEvent) => void, onDelete?: (e?: React.MouseEvent) => void }) {
  const setShowBottomNav = useSetAtom(showBottomNavAtom);
  useEffect(() => {
    setShowBottomNav(!isOpen);
    return () => setShowBottomNav(true);
  }, [isOpen, setShowBottomNav]);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="bottom"
        className="bg-[#FBF6F1] rounded-t-xl px-4 pt-3 pb-6 max-h-[85vh] overflow-y-auto"
      >
        <div className="w-full mx-auto flex flex-col justify-start items-center gap-1.5">
          <div className="w-20 h-[5px]  rounded-[100px] opacity-20"></div>
          <div className="self-stretch bg-[#FBF6F1] rounded-md shadow flex flex-col justify-start items-start mt-4">
            <div className="self-stretch px-4 py-2 flex flex-col justify-start items-start gap-3">
              <button 
                onClick={e => { e.stopPropagation(); onEdit?.(e); }}
                className="inline-flex justify-start items-center gap-2 w-full"
              >
                <div className="w-6 h-6 flex items-center justify-center -ml-2">
                  <Edit className="w-4 h-4" />
                </div>
                <div className="justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-none ml-1">Chỉnh sửa bài viết</div>
              </button>
              <button 
                onClick={e => { e.stopPropagation(); onDelete?.(e); }}
                className="inline-flex justify-start items-center gap-2 w-full"
              >
                <div className="w-6 h-6 flex items-center justify-center -ml-2">
                  <Trash className="w-4 h-4" />
                </div>
                <div className="justify-start text-[#F04438] text-sm font-normal font-sora leading-none ml-1">Xóa bài viết</div>
              </button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
